import React, { useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Card, Container } from 'react-bootstrap';
import ability from '../../pages/config/defineAbility';
import { PERMS } from '../../pages/config/perms';
import { ADMIN_ROLE } from '../../Constants';
import AppContext from '../../AppContext';
import { getEncryptedUserData } from '../../services/Utils';

const HRMMenu = () => {
  const history = useHistory();
  const global = useContext(AppContext);

  // Helper function to check if user is admin
  const isAdmin = () => {
    try {
      const user = getEncryptedUserData();
      if (!user) return false;
      // Check multiple possible role fields
      const userRole = user.role || user.role_id || user.user_id;
      return parseInt(userRole) === ADMIN_ROLE || userRole === ADMIN_ROLE;
    } catch (e) {
      return false;
    }
  };

  // Helper function to check if user has specific permission (even admins need explicit permission)
  const hasPermission = (permissionId) => {
    try {
      const user = getEncryptedUserData();
      if (!user || !user.rights || !Array.isArray(user.rights)) return false;
      
      // Check if user has the specific permission in their rights
      return user.rights.some((right) => {
        const resourceId = right.resource_id || right.id;
        return resourceId === permissionId;
      });
    } catch (e) {
      return false;
    }
  };

  // Define gradient colors for each section
  const sectionGradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  ];

  // Define menu items grouped by category
  const menuCategories = [
    {
      title: 'Employee Management',
      items: [
        {
          label: 'Employee Listing',
          path: '/hrm/employee-list',
          permission: () => ability.can(PERMS.READ, PERMS.LIST_EMPLOYEES),
        },
        {
          label: 'Sectors',
          path: '/hrm/sectors',
          permission: () => isAdmin(),
        },
        {
          label: 'Staff File Types',
          path: '/hrm/staff-file-types',
          permission: () => isAdmin(),
        },
        {
          label: 'Scoring Periods',
          path: '/hrm/scoring-periods',
          permission: () => isAdmin(),
        },
        {
          label: 'Staff Scoring Criteria',
          path: '/hrm/staff-scoring-criteria',
          permission: () => isAdmin(),
        },
        {
          label: 'Staff Scoring',
          path: '/hrm/staff-scoring',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_ACCESS_STAFF_SCORING),
        },
        {
          label: 'Staff Ratings by Period',
          path: '/hrm/staff-ratings-by-period',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_ACCESS_STAFF_SCORING_RESULTS),
        },
     
        {
          label: 'Employee Designations',
          path: '/hrm/upload-designations',
          permission: () => ability.can(PERMS.CREATE, PERMS.UPLOAD_DESIGNATIONS),
        },
        {
          label: 'Upload Employees',
          path: '/hrm/upload-employees',
          permission: () => ability.can(PERMS.CREATE, PERMS.UPLOAD_EMPLOYEES),
        },
        {
          label: 'Upload Offices',
          path: '/hrm/upload-offices',
          permission: () => ability.can(PERMS.CREATE, PERMS.UPLOAD_OFFICES),
        },
        {
          label: 'Upload Bank Accounts',
          path: '/hrm/upload-bank-accounts',
          permission: () => ability.can(PERMS.CREATE, PERMS.UPLOAD_BANK_ACCOUNTS),
        },
        {
          label: 'Staff ID Cards',
          path: '/hrm/staff-id-cards',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_STAFF_ID_CARD_DETAILS),
        },
        {
          label: 'Workforce Planning',
          path: '/hrm/workforce-planning',
          permission: () => ability.can(PERMS.CREATE, PERMS.CAN_CREATE_WORKFORCE_PLANNING),
        },
        {
          label: 'Workforce Planning Analysis',
          path: '/hrm/workforce-planning/analysis',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_WORKFORCE_PLANNING_ANALYSIS),
        },
      ],
    },
    {
      title: 'Staff Attendance',
      items: [
        {
          label: 'Daily Attendance Report',
          path: '/hrm/attendance/daily-attendance',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_STAFF_ATTENDANCE),
        },
        {
          label: 'Attendance Summary',
          path: '/hrm/attendance/attendance-summary',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_STAFF_ATTENDANCE),
        },
        {
          label: 'Exceptional InAttendance',
          path: '/hrm/exceptional-inattendance',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_STAFF_ATTENDANCE),
        },
      ],
    },
    {
      title: 'Payroll Management',
      items: [
        {
          label: 'Upload Budget Allocations',
          path: '/hrm/upload-budget-allocations',
          permission: () => ability.can(PERMS.CREATE, PERMS.UPLOAD_BUDGET_ALLOCATIONS),
        },
        {
          label: 'Create Budget Allocations',
          path: '/hrm/payroll/create-allocations',
          permission: () => ability.can(PERMS.CREATE, PERMS.CREATE_BUDGET_ALLOCATIONS),
        },
        {
          label: 'Process Payroll',
          path: '/hrm/payroll/process-payroll',
          permission: () => ability.can(PERMS.CREATE, PERMS.CAN_PROCESS_PAYROLL),
        },
        {
          label: 'Draft Payroll',
          path: '/hrm/payroll-selection',
          permission: () => ability.can(PERMS.UPDATE, PERMS.CAN_UPDATE_PAYROLL),
        },
        {
          label: 'Completed Payroll',
          path: '/hrm/completed-payroll',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_COMPLETED_PAYROLL),
        },
        {
          label: 'Create Budget Codes',
          path: '/hrm/payroll/create-budget-codes',
          permission: () => ability.can(PERMS.CREATE, PERMS.CAN_CREATE_BUDGET_CODES),
        },
        {
          label: 'Assign Allowances',
          path: '/hrm/payroll/assign-allowances',
          permission: () => ability.can(PERMS.UPDATE, PERMS.CAN_ASSIGN_STAFF_ALLOWANCE),
        },
        {
          label: 'Assign Staff Tax',
          path: '/hrm/payroll/assign-staff-tax',
          permission: () => ability.can(PERMS.UPDATE, PERMS.CAN_ASSIGN_STAFF_TAX),
        },
        {
          label: 'Assign Tags',
          path: '/hrm/payroll/assign-tags',
          permission: () => ability.can(PERMS.UPDATE, PERMS.CAN_ASSIGN_STAFF_TAGS),
        },
        {
          label: 'Pending Salary Advance',
          path: '/hrm/payroll/pending-advances',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_PENDING_ADVANCE_REQUESTS),
        },
        {
          label: 'Budget Allocations Report',
          path: '/hrm/budget-allocations-report',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_BUDGET_ALLOCATIONS_REPORT),
        },
        {
          label: 'Payroll Summary',
          path: '/hrm/payroll-summary',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_PAYROLL_SUMMARY),
        },
        {
          label: 'Download Payslips',
          path: '/hrm/generate-payslips',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_DOWNLOAD_PAYSLIPS),
        },
        {
          label: 'Contract TrackSheet',
          path: '/hrm/contract-tracksheet',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_ACCESS_CONTRACT_TRACKSHEET),
        },
      ],
    },
    {
      title: 'Leave & Toil Management',
      items: [
        {
          label: 'Create Leave',
          path: '/hrm/leave/create',
          permission: () => true,
        },
        {
          label: 'My Leave Requests',
          path: '/hrm/leave/my-leave-requests',
          permission: () => true,
        },
        {
          label: 'Pending Leave Actions',
          path: '/hrm/leave/pending-actions',
          permission: () => true,
        },
        {
          label: 'Create Toil',
          path: '/hrm/toil/create',
          permission: () => true,
        },
        {
          label: 'My Toil Requests',
          path: '/hrm/toil/my-requests',
          permission: () => true,
        },
        {
          label: 'Pending Toil Requests',
          path: '/hrm/toil/pending-toil-actions',
          permission: () => true,
        },
        {
          label: 'Pending Toil Claims',
          path: '/hrm/toil/pending-toil-claims',
          permission: () => true,
        },
        {
          label: 'Leave Requests Report',
          path: '/hrm/leave/list-requests',
          permission: () => ability.can(PERMS.READ, PERMS.LEAVE_REQUEST_REPORT),
        },
        {
          label: 'Leave Balance Report',
          path: '/hrm/leave/balances',
          permission: () => ability.can(PERMS.READ, PERMS.CAN_VIEW_LEAVE_BALANCE_REPORT),
        },
      ],
    },
    {
      title: 'Setup',
      items: [
        {
          label: 'Role Departments',
          path: '/hrm/role-departments',
          permission: () => ability.can(PERMS.UPDATE, PERMS.MANAGE_STAFF_ROLES),
        },
      ],
    },
    {
      title: 'LMS Reports',
      items: [
        
      ],
    },
  ];

  // Filter items based on permissions
  const getVisibleItems = (items) => {
    return items.filter((item) => item.permission());
  };

  const handleButtonClick = (path) => {
    history.push(path);
  };

  return (
    <Container fluid className="mt-4">
      <div className="page-header d-print-none mb-4">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">HRM</h2>
              {/* <div className="text-muted mt-1">Select a menu option to continue</div> */}
            </div>
          </div>
        </div>
      </div>

      <div className="container-xl">
        <Card className="shadow-sm" style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Card.Body style={{ padding: '2rem' }}>
            {menuCategories.map((category, categoryIndex) => {
              const visibleItems = getVisibleItems(category.items);
              if (visibleItems.length === 0) return null;

              return (
                <div key={categoryIndex} className="mb-4" style={{ marginBottom: categoryIndex < menuCategories.length - 1 ? '2.5rem' : '0' }}>
                  {/* Section Title with Gradient */}
                  <div
                    style={{
                      background: sectionGradients[categoryIndex % sectionGradients.length],
                      padding: '0.75rem 1.25rem',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <h5
                      className="mb-0"
                      style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '1.1rem',
                        textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      }}
                    >
                      {category.title}
                    </h5>
                  </div>

                  {/* Buttons Grid */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                      gap: '0.75rem',
                    }}
                  >
                    {visibleItems.map((item, itemIndex) => {
                      const buttonNumber = itemIndex + 1;
                      return (
                        <button
                          key={itemIndex}
                          onClick={() => handleButtonClick(item.path)}
                          style={{
                            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem 1rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            color: '#2d3748',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            minHeight: '36px',
                            display: 'flex',
                            alignItems: 'center',
                            textAlign: 'left',
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                            e.target.style.color = 'white';
                            e.target.style.transform = 'translateY(-2px)';
                            e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                            const span = e.target.querySelector('span');
                            if (span) {
                              span.style.background = 'rgba(255, 255, 255, 0.3)';
                              span.style.color = 'white';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)';
                            e.target.style.color = '#2d3748';
                            e.target.style.transform = 'translateY(0)';
                            e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                            const span = e.target.querySelector('span');
                            if (span) {
                              span.style.background = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
                              span.style.color = 'white';
                            }
                          }}
                        >
                          <span style={{ 
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            fontSize: '0.75rem',
                            fontWeight: '700',
                            marginRight: '0.75rem',
                            flexShrink: 0,
                            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                            transition: 'all 0.3s ease',
                          }}>
                            {buttonNumber}
                          </span>
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default HRMMenu;
